'use client';

import { QueryKeys } from '@/constants/query-keys';
import { theaterShowtimesApiService } from '@/services/(frontend)/showtimes/theater.showtimes-api.service';
import { TheaterShowtimeItem } from '@/types/showtimes';
import { toDateLike } from '@/utils/date-time-helper';
import { useQuery } from '@tanstack/react-query';
import { useIsAuthReady } from '../profile/is-auth-ready';
import { useDynamicStaleTime } from '../shared/dynamic-stale-time';

type UseShowtimesParams = {
  date?: Date;
  initialIsAuthenticated?: boolean;
  movieIds?: number[];
  theaterId: number;
  timeZone?: string;
};

export const useTheaterShowtimeDates = (
  theaterId: number,
  timeZone?: string
): ReturnType<typeof useQuery<string[] | undefined>> => {
  const updateQueryStaleTime = useDynamicStaleTime();
  const queryKey = QueryKeys.global.theaterShowtimeDates(theaterId.toString(), timeZone);

  return useQuery({
    enabled: theaterId > 0,
    queryFn: async ({ signal }) => {
      const response = await theaterShowtimesApiService.getAvailableDates(theaterId, timeZone, signal);
      updateQueryStaleTime(queryKey, response.headers);
      return response.data;
    },
    queryKey,
  });
};

export const useTheaterShowtimes = (params: UseShowtimesParams): ReturnType<typeof useQuery<TheaterShowtimeItem[] | undefined>> => {
  const { date, initialIsAuthenticated, movieIds, theaterId, timeZone } = params;
  const hasMovieIdsFilter = movieIds !== undefined;
  const hasRequiredMovieIds = !hasMovieIdsFilter || movieIds.length > 0;
  const { isAuthenticated, isLoading: isAuthLoading } = useIsAuthReady();
  const updateQueryStaleTime = useDynamicStaleTime();
  const queryIsAuthenticated = isAuthLoading && initialIsAuthenticated !== undefined
    ? initialIsAuthenticated
    : isAuthenticated;
  const queryKey = QueryKeys.global.theaterShowtimes(
    theaterId.toString(),
    date ? toDateLike(date).toISOString() : undefined,
    movieIds,
    timeZone,
    queryIsAuthenticated
  );

  return useQuery({
    enabled: theaterId > 0 && hasRequiredMovieIds,
    queryFn: async ({ signal }) => {
      const response = await theaterShowtimesApiService.getShowtimes({ date, movieIds, theaterId, timeZone }, signal);
      updateQueryStaleTime(queryKey, response.headers);
      return response?.data;
    },
    queryKey,
  });
};
